import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, FileText, Target, Briefcase, Edit, Eye, Trash2, PlusCircle } from 'lucide-react';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { AuthContext } from '../../context/AuthContext';
import * as resumeService from '../../services/resumeService';
import * as aiService from '../../services/aiService';
import * as creditService from '../../services/creditService';
import { formatDate, timeAgo } from '../../utils/formatters';
import CreditsPurchaseModal from '../../components/Credits/CreditsPurchaseModal';

export default function Dashboard() {
	const { user, credits, updateCredits } = useContext(AuthContext);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [resumes, setResumes] = useState([]);
	const [totalResumes, setTotalResumes] = useState(0);
	const [analyses, setAnalyses] = useState([]);
	const [openPurchase, setOpenPurchase] = useState(false);
	const [lastReset, setLastReset] = useState(null);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				// Load up to 100 resumes to compute average ATS
				const resResumes = await resumeService.getAllResumes(1, 100);
				if (mounted) {
					setResumes(resResumes.resumes.slice(0, 5));
					setTotalResumes(resResumes.total || 0);
				}
				const resAnalyses = await aiService.getAnalysisHistory(1, 3);
				if (mounted) setAnalyses(resAnalyses.analyses || []);
				const bal = await creditService.getBalance();
				if (mounted) setLastReset(bal.dailyCreditsResetDate || null);
				await updateCredits();
			} catch {
				// ignore; errors will be reflected in empty states
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const avgATS = useMemo(() => {
		const list = resumes.length ? resumes : [];
		if (!list.length) return 0;
		const sum = list.reduce((acc, r) => acc + (Number(r.atsScore) || 0), 0);
		return Math.round((sum / list.length) || 0);
	}, [resumes]);

	const atsColor = avgATS > 75 ? 'text-green-600' : avgATS >= 50 ? 'text-yellow-600' : 'text-red-600';

	const nextResetText = useMemo(() => {
		if (!lastReset) return '';
		const next = new Date(new Date(lastReset).getTime() + 24 * 60 * 60 * 1000);
		const diffMs = next.getTime() - Date.now();
		if (diffMs <= 0) return 'Resets soon';
		const hours = Math.floor(diffMs / 3600000);
		const mins = Math.floor((diffMs % 3600000) / 60000);
		return `${hours}h ${mins}m`;
	}, [lastReset]);

	async function handleDelete(id) {
		try {
			await resumeService.deleteResume(id);
			const resResumes = await resumeService.getAllResumes(1, 100);
			setResumes(resResumes.resumes.slice(0, 5));
			setTotalResumes(resResumes.total || 0);
		} catch {
			// ignore
		}
	}

	if (loading) {
		return (
			<DashboardLayout>
				<LoadingSpinner fullScreen />
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<header>
					<h1 className="text-2xl font-bold">Welcome back, {user?.name?.split(' ')?.[0] || 'there'}!</h1>
					<p className="text-gray-600">Here’s a quick overview of your resume progress.</p>
				</header>

				{/* Stats */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<Card>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500">Credits Balance</p>
								<p className="mt-1 text-3xl font-bold">{credits}</p>
								<div className="mt-2 flex items-center gap-2">
									<Button size="sm" onClick={() => setOpenPurchase(true)}>
										Buy More Credits
									</Button>
									{credits < 5 && nextResetText && (
										<span className="text-xs text-gray-500">Daily reset in {nextResetText}</span>
									)}
								</div>
							</div>
							<Coins className="w-10 h-10 text-primary-600" />
						</div>
					</Card>
					<Card>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500">Total Resumes</p>
								<p className="mt-1 text-3xl font-bold">{totalResumes}</p>
								<button
									className="mt-2 text-sm text-primary-600 hover:underline"
									onClick={() => navigate('/resumes')}
								>
									View All
								</button>
							</div>
							<FileText className="w-10 h-10 text-primary-600" />
						</div>
					</Card>
					<Card>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500">Average ATS Score</p>
								<p className={`mt-1 text-3xl font-bold ${atsColor}`}>{avgATS}%</p>
							</div>
							<Target className="w-10 h-10 text-primary-600" />
						</div>
					</Card>
					<Card>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500">Job Matches</p>
								<p className="mt-1 text-3xl font-bold">0</p>
								<button
									className="mt-2 text-sm text-primary-600 hover:underline"
									onClick={() => navigate('/job-matcher')}
								>
									View Matches
								</button>
							</div>
							<Briefcase className="w-10 h-10 text-primary-600" />
						</div>
					</Card>
				</div>

				{/* Recent resumes */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold">Recent Resumes</h2>
						<Button variant="ghost" onClick={() => navigate('/resumes')}>
							View All
						</Button>
					</div>
					<div className="bg-white rounded-lg border border-gray-100 shadow overflow-hidden">
						{!resumes.length ? (
							<div className="p-6 text-sm text-gray-500">No resumes yet. Create your first one!</div>
						) : (
							<table className="min-w-full text-sm">
								<thead className="bg-gray-50">
									<tr>
										<th className="text-left px-4 py-2">Title</th>
										<th className="text-left px-4 py-2">Template</th>
										<th className="text-left px-4 py-2">ATS Score</th>
										<th className="text-left px-4 py-2">Last Modified</th>
										<th className="text-left px-4 py-2">Actions</th>
									</tr>
								</thead>
								<tbody>
									{resumes.map((r) => (
										<tr key={r._id} className="border-t">
											<td className="px-4 py-2">{r.title}</td>
											<td className="px-4 py-2 capitalize">{r.templateType}</td>
											<td className="px-4 py-2">{r.atsScore || 0}%</td>
											<td className="px-4 py-2">{timeAgo(r.updatedAt)} ({formatDate(r.updatedAt)})</td>
											<td className="px-4 py-2">
												<div className="flex items-center gap-2">
													<button className="p-1 hover:bg-gray-100 rounded" title="Edit" onClick={() => navigate(`/resumes/${r._id}/edit`)}>
														<Edit className="w-4 h-4" />
													</button>
													<button className="p-1 hover:bg-gray-100 rounded" title="Preview" onClick={() => navigate(`/resumes/${r._id}/preview`)}>
														<Eye className="w-4 h-4" />
													</button>
													<button className="p-1 hover:bg-gray-100 rounded text-red-600" title="Delete" onClick={() => handleDelete(r._id)}>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>

				{/* Recent analysis */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold">Recent Analyses</h2>
						<Button variant="ghost" onClick={() => navigate('/ats-analyzer')}>
							Run Analysis
						</Button>
					</div>
					<div className="bg-white rounded-lg border border-gray-100 shadow overflow-hidden">
						{!analyses.length ? (
							<div className="p-6 text-sm text-gray-500">No analyses yet.</div>
						) : (
							<ul className="divide-y">
								{analyses.map((a) => (
									<li key={a._id} className="p-4 flex items-center justify-between">
										<div>
											<p className="font-medium">{a.resumeTitle || 'Resume'}</p>
											<p className="text-sm text-gray-500">{formatDate(a.analyzedAt)}</p>
										</div>
										<div className="text-right">
											<p className={`font-bold ${a.atsScore > 75 ? 'text-green-600' : a.atsScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
												{a.atsScore}%
											</p>
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>

				{/* Quick actions */}
				<div className="space-y-3">
					<h2 className="text-lg font-semibold">Quick Actions</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<ActionCard icon={PlusCircle} title="Create New Resume" onClick={() => navigate('/resumes/new')} />
						<ActionCard icon={Target} title="Analyze Resume" onClick={() => navigate('/ats-analyzer')} />
						<ActionCard icon={Briefcase} title="Match Jobs" onClick={() => navigate('/job-matcher')} />
						<ActionCard icon={Coins} title="Buy Credits" onClick={() => setOpenPurchase(true)} />
					</div>
				</div>
			</div>
			<CreditsPurchaseModal isOpen={openPurchase} onClose={() => setOpenPurchase(false)} onPurchased={updateCredits} />
		</DashboardLayout>
	);
}

function ActionCard({ icon: Icon, title, onClick }) {
	return (
		<button onClick={onClick} className="bg-white rounded-lg border border-gray-100 shadow p-5 flex items-center gap-3 hover:shadow-md">
			<Icon className="w-6 h-6 text-primary-600" />
			<span className="font-medium">{title}</span>
		</button>
	);
}


